import { Feature } from '@frontend/functionality/types';
import { TransactionStateName } from '@frontend/transaction/types';
import { FormattedMessage } from 'react-intl';

export function getNivalizTypeLabel(feature: Feature | string | null | undefined): React.ReactNode {
    if (feature === Feature.INBOUND_TRANSACTION) {
        return (
            <FormattedMessage
                id='feature.nivaliz.transactions.type.coffin'
                description='Label for Nivaliz coffin (inbound) transaction type'
                defaultMessage='Coffin'
            />
        );
    }
    if (feature === Feature.OUTBOUND_TRANSACTION) {
        return (
            <FormattedMessage
                id='feature.nivaliz.transactions.type.urn'
                description='Label for Nivaliz urn (outbound) transaction type'
                defaultMessage='Urn'
            />
        );
    }
    return null;
}

/** Pick-up / drop-off chip for list cards; omitted for non-pending states. */
export function getNivalizActionLabel(state: TransactionStateName | string | null | undefined): React.ReactNode {
    if (state === TransactionStateName.TRANSACTION_PICK_UP_PENDING) {
        return (
            <FormattedMessage
                id='Buttons.PICK_UP'
                description='Pick up text on a button'
                defaultMessage='Pick-up'
            />
        );
    }
    if (state === TransactionStateName.TRANSACTION_DROP_OFF_PENDING) {
        return (
            <FormattedMessage
                id='Buttons.DROP_OFF'
                description='Drop off text on a button'
                defaultMessage='Drop-Off'
            />
        );
    }
    return null;
}

interface NivalizListCardTitleArgs {
    deceasedName?: string | null;
    shippingNotes?: string | null;
    trackingNumber?: string | null;
}

/** Condensed list title: deceased name, else notes, else tracking number, else short fallback. */
export function getNivalizListCardTitle({ deceasedName, shippingNotes, trackingNumber }: NivalizListCardTitleArgs): React.ReactNode {
    const deceased = deceasedName?.trim();
    if (deceased) {
        return deceased;
    }

    const notes = shippingNotes?.trim();
    if (notes) {
        return notes;
    }

    const tracking = trackingNumber?.trim();
    if (tracking) {
        return tracking;
    }

    return (
        <FormattedMessage
            id='nivaliz.transaction.default'
            defaultMessage='Nivaliz transaction'
        />
    );
}

interface NivalizTransactionTitleArgs {
    feature: Feature | string | null | undefined;
    senderName?: string | null;
    receiverName?: string | null;
    isSender?: boolean;
    isReceiver?: boolean;
}

/**
 * Corporate list/detail title: deceased name or tracking number first,
 * otherwise coffin/urn from-sender / for-receiver wording.
 */
export function getNivalizCorporateTitle({
    deceasedName,
    trackingNumber,
    feature,
    senderName,
    receiverName
}: Pick<NivalizListCardTitleArgs, 'deceasedName' | 'trackingNumber'> &
    Pick<NivalizTransactionTitleArgs, 'feature' | 'senderName' | 'receiverName'>): React.ReactNode {
    if (deceasedName?.trim() || trackingNumber?.trim()) {
        return getNivalizListCardTitle({ deceasedName, trackingNumber });
    }
    return getNivalizTransactionTitle({ feature, senderName, receiverName });
}

/** Personal uses role; corporate omits roles and always shows from-sender / for-receiver. */
export function getNivalizTransactionTitle({
    feature,
    senderName,
    receiverName,
    isSender = false,
    isReceiver = false
}: NivalizTransactionTitleArgs): React.ReactNode {
    const sender = senderName ?? '/';
    const receiver = receiverName ?? '/';

    if (feature === Feature.INBOUND_TRANSACTION) {
        if (isSender) {
            return (
                <FormattedMessage
                    id='nivaliz.transaction.coffin_for_receiver'
                    defaultMessage='Coffin for {receiver}'
                    values={{ receiver }}
                />
            );
        }
        return (
            <FormattedMessage
                id='nivaliz.transaction.coffin_from_sender'
                defaultMessage='Coffin from {sender}'
                values={{ sender }}
            />
        );
    }

    if (feature === Feature.OUTBOUND_TRANSACTION) {
        if (isReceiver) {
            return (
                <FormattedMessage
                    id='nivaliz.transaction.urn_from_sender'
                    defaultMessage='Urn from {sender}'
                    values={{ sender }}
                />
            );
        }
        return (
            <FormattedMessage
                id='nivaliz.transaction.urn_for_receiver'
                defaultMessage='Urn for {receiver}'
                values={{ receiver }}
            />
        );
    }

    return (
        <FormattedMessage
            id='nivaliz.transaction.default'
            defaultMessage='Nivaliz transaction'
        />
    );
}
